import React, { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { Point } from '@/types/common';
import { ScaleData } from '@/types/floorPlanViewer';
import { extractWallsFromPolygon, Wall } from '@/utils/wallDetection';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';

interface WallSelectionModeProps {
  rooms: Array<{
    id: string;
    name: string;
    points: Point[];
  }>;
  scale: number;
  scaleData: ScaleData | null;
  isActive: boolean;
  onWallSelect: (roomId: string, roomName: string, wall: Wall) => void;
  viewportTransform: { x: number; y: number; scale: number };
  floorPlanId: string;
}

export function WallSelectionMode({
  rooms,
  scale,
  scaleData,
  isActive,
  onWallSelect,
  viewportTransform,
  floorPlanId
}: WallSelectionModeProps) {
  const [hoveredWall, setHoveredWall] = useState<{ roomId: string; wall: Wall } | null>(null);
  const [existingElevations, setExistingElevations] = useState<Map<string, any>>(new Map());
  
  // Fetch existing elevations for this floor plan
  useEffect(() => {
    const fetchElevations = async () => {
      try {
        const supabase = await getAuthenticatedSupabaseClient();
        const { data, error } = await supabase
          .from('elevations')
          .select('id, room_id, wall_index, name')
          .eq('floor_plan_id', floorPlanId);
        
        if (error) {
          logger.error('Error fetching elevations:', error);
          return;
        }
        
        // Map elevations by room_id-wall_index for quick lookup
        const elevationMap = new Map();
        data?.forEach(elevation => {
          const key = `${elevation.room_id}-${elevation.wall_index}`;
          elevationMap.set(key, elevation);
        });
        
        setExistingElevations(elevationMap);
      } catch (error) {
        logger.error('Error fetching elevations:', error);
      }
    };
    
    if (floorPlanId) {
      fetchElevations();
    }
  }, [floorPlanId]);

  // Extract walls from all rooms
  const roomWalls = React.useMemo(() => {
    // Debug: Log first room's points to check coordinates
    if (rooms.length > 0 && rooms[0].points.length > 0) {
      logger.log('[WallSelectionMode] First room points:', rooms[0].points);
    }
    return rooms.map(room => ({
      roomId: room.id,
      roomName: room.name,
      walls: extractWallsFromPolygon(room.points)
    }));
  }, [rooms]);


  if (!isActive) return null;

  return (
    <g
      className="wall-selection-layer"
      style={{ pointerEvents: isActive ? 'all' : 'none' }}
    >
      {/* Render wall highlights */}
      {roomWalls.map(room => (
        <g key={room.roomId}>
          {room.walls.map(wall => {
            const isHovered = hoveredWall?.roomId === room.roomId && 
                            hoveredWall?.wall.index === wall.index;
            
            // Check if this wall has an existing elevation
            const elevationKey = `${room.roomId}-${wall.index}`;
            const hasElevation = existingElevations.has(elevationKey);
            const elevation = existingElevations.get(elevationKey);
            
            // Determine wall color based on state
            let wallColor = "rgb(168, 85, 247)"; // Purple for no elevation
            if (hasElevation) {
              wallColor = "rgb(34, 197, 94)"; // Green for has elevation
            }
            if (isHovered) {
              wallColor = "rgb(59, 130, 246)"; // Blue for hover
            }
            
            // Calculate wall midpoint for icon placement
            const midX = (wall.startPoint.x + wall.endPoint.x) / 2;
            const midY = (wall.startPoint.y + wall.endPoint.y) / 2;
            
            return (
              <g 
                key={`${room.roomId}-wall-${wall.index}`}
                onMouseEnter={() => {
                  setHoveredWall({ roomId: room.roomId, wall });
                }}
                onMouseLeave={() => {
                  setHoveredWall(null);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  logger.log('[WallSelectionMode] Wall clicked', { roomId: room.roomId, wall, hasElevation });
                  onWallSelect(room.roomId, room.roomName, wall);
                }}
                style={{ cursor: 'pointer' }}
              >
                {/* Always show wall outline in elevation mode */}
                <line
                  x1={wall.startPoint.x}
                  y1={wall.startPoint.y}
                  x2={wall.endPoint.x}
                  y2={wall.endPoint.y}
                  stroke={wallColor}
                  strokeWidth={isHovered ? 5 / scale : 4 / scale}
                  strokeLinecap="round"
                  strokeDasharray={hasElevation ? "none" : (isHovered ? "none" : `${8/scale} ${4/scale}`)}
                  opacity={isHovered ? 1 : hasElevation ? 0.9 : 0.8}
                  pointerEvents="none"
                />
                
                {/* Invisible thick line for better hit detection */}
                <line
                  x1={wall.startPoint.x}
                  y1={wall.startPoint.y}
                  x2={wall.endPoint.x}
                  y2={wall.endPoint.y}
                  stroke="transparent"
                  strokeWidth={20 / scale}
                />
                
                {/* Elevation indicator icon */}
                {hasElevation && (
                  <g transform={`translate(${midX}, ${midY})`}>
                    {/* Background circle */}
                    <circle
                      r={12 / scale}
                      fill="white"
                      stroke={wallColor}
                      strokeWidth={2 / scale}
                    />
                    {/* Icon - simplified elevation symbol */}
                    <rect
                      x={-6 / scale}
                      y={-6 / scale}
                      width={12 / scale}
                      height={12 / scale}
                      fill="none"
                      stroke={wallColor}
                      strokeWidth={1.5 / scale}
                    />
                    <line
                      x1={-6 / scale}
                      y1={0}
                      x2={6 / scale}
                      y2={0}
                      stroke={wallColor}
                      strokeWidth={1 / scale}
                    />
                  </g>
                )}
                
                {/* Additional hover effects */}
                {isHovered && (
                  <>
                    {/* Glow effect */}
                    <line
                      x1={wall.startPoint.x}
                      y1={wall.startPoint.y}
                      x2={wall.endPoint.x}
                      y2={wall.endPoint.y}
                      stroke="rgb(59, 130, 246)"
                      strokeWidth={6 / scale}
                      opacity={0.2}
                      strokeLinecap="round"
                    />
                    
                    {/* Wall endpoints */}
                    <circle
                      cx={wall.startPoint.x}
                      cy={wall.startPoint.y}
                      r={4 / scale}
                      fill="rgb(59, 130, 246)"
                    />
                    <circle
                      cx={wall.endPoint.x}
                      cy={wall.endPoint.y}
                      r={4 / scale}
                      fill="rgb(59, 130, 246)"
                    />
                    
                    {/* Tooltip showing elevation name if exists */}
                    {hasElevation && (
                      <g transform={`translate(${midX}, ${midY - 20 / scale})`}>
                        <rect
                          x={-50 / scale}
                          y={-10 / scale}
                          width={100 / scale}
                          height={20 / scale}
                          fill="rgba(0, 0, 0, 0.8)"
                          rx={3 / scale}
                        />
                        <text
                          x={0}
                          y={3 / scale}
                          fill="white"
                          fontSize={10 / scale}
                          textAnchor="middle"
                          style={{ userSelect: 'none' }}
                        >
                          {elevation.name}
                        </text>
                      </g>
                    )}
                  </>
                )}
              </g>
            );
          })}
        </g>
      ))}
      
    </g>
  );
}
